import * as React from 'react';
import classnames from 'classnames';
import isString from 'lodash/isString';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import PopoverList from 'components/popover-list/PopoverList';

import SubcampaignOfferActions from 'modules/subcampaigns/components/SubcampaignOfferActions';

import SystemMessageWrapperContext from './context/SystemMessageWrapperContext';
import SystemMessageNegotiationsReopened from '../negotiations-reopened/SystemMessageNegotiationsReopened';
import PayoutRescheduledBusinessAndBankDetailsUnverified from '../payout-rescheduled/payout-rescheduled-business-and-bank-details-unverified/PayoutRescheduledBusinessAndBankDetailsUnverified';
import PayoutRescheduledBusinessAndBankDetailsIncomplete from '../payout-rescheduled/payout-rescheduled-business-and-bank-details-incomplete/PayoutRescheduledBusinessAndBankDetailsIncomplete';
import PayoutRescheduledInvoiceNotReceived from '../payout-rescheduled/payout-rescheduled-invoice-not-received/PayoutRescheduledInvoiceNotReceived';
import PayoutRescheduledAccidentalRelease from '../payout-rescheduled/payout-rescheduled-accidental-release/PayoutRescheduledAccidentalRelease';
import PayoutRescheduledOther from '../payout-rescheduled/payout-rescheduled-other/PayoutRescheduledOther';
import SystemMessagePaymentDetailsAreEmpty from '../payment-details-are-empty/SystemMessagePaymentDetailsAreEmpty';
import SystemMessageCampaignEnded from '../campaign-ended/SystemMessageCampaignEnded';
import SystemMessageCcmOutfitDeclined from '../ccm-outfit-declined/SystemMessageCcmOutfitDeclined';
import SystemMessageCcmCampaignParticipation from '../ccm-campaign-participation/SystemMessageCcmCampaignParticipation';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDateAndTime } from 'utils/date';
import { sanitizeGenericHtml } from 'utils/sanitize';
import { stripHTML } from 'utils/helper/common';

import './system-message-wrapper.scss';

applyTranslations(require('./system-message-wrapper.i18n.json'));

import type {
  Campaign,
  MessengerMessage,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';
import type {
  SystemMessageWrapperContext as SystemMessageWrapperContextAlias
} from './types';

type Props = {
  campaign: Campaign,
  subcampaign: Subcampaign,
  message: MessengerMessage,
  messageType: string,
  influencerProfile: SubcampaignInfluencerProfile,
  isBrand?: boolean,
  latestMessageExternalIdForInfluencerAction: string | undefined | null
};

const MSG_KEY = 'system_message_wrapper';

const SystemMessageWrapper: React.FC<Props> = ({
  campaign,
  subcampaign,
  message,
  messageType,
  influencerProfile,
  isBrand,
  latestMessageExternalIdForInfluencerAction
}: Props) => {
  const userType = isBrand ? 'brand' : 'creator';
  const updatedMessage = { ...message };

  // use "files" when it's task type "other"
  if (updatedMessage.metadata?.postType === 'other') {
    updatedMessage.metadata.type = updatedMessage.metadata.type.replace(/insights/ig, 'files');
  }

  const renderAvatar = (
    iconName: string,
    colorOverride?: string
  ): React.ReactNode => (
    <div className="system-message-wrapper__avatar">
      <SVGDynamicIcon
        name={iconName}
        width={55}
        height={55}
        customColor={colorOverride || 'blue'}
      />
    </div>
  );

  const renderPopover = (
    body: string,
    title?: string
  ): React.ReactNode => {
    const popoverClasses = classnames('system-message-wrapper__popover', {
      [`system-message-wrapper__popover--${userType}`]: true
    });

    const Body = (
      <div dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(body) }} />
    );

    return (
      <PopoverList
        list={[{
          title,
          body: Body
        }]}
        width="wide"
        classNamePopover={popoverClasses}
      />
    );
  };

  const renderFooter = (): React.ReactNode => {
    const time = getDisplayDateAndTime(updatedMessage.createdAt);
    const text = `${__t(`${MSG_KEY}.labels.system_message`)} ${time}`;

    return text ? (
      <div
        className="messages-system-message__footer"
        title={text}
      >
        {text}
      </div>
    ) : null;
  };

  const renderDisclaimer = (
    disclaimer: string,
    disclaimerPopoverBody?: string,
    disclaimerPopoverTitle?: string
  ): React.ReactNode => {
    if (!isString(disclaimer)) {
      return null;
    }

    const disclaimerClasses = classnames('system-message-wrapper__disclaimer', {
      [`system-message-wrapper__disclaimer--${userType}`]: true
    });

    return (
      <div className={disclaimerClasses}>
        <span
          title={stripHTML(disclaimer)}
          dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(disclaimer) }}
        />
        {disclaimerPopoverBody && renderPopover(disclaimerPopoverBody, disclaimerPopoverTitle)}
      </div>
    );
  };

  const renderInfluencerActions = (): React.ReactNode => {
    if (updatedMessage.externalId !== latestMessageExternalIdForInfluencerAction) {
      return null;
    }

    return (
      <SubcampaignOfferActions
        buttonWidth="normal"
        subcampaign={subcampaign}
        className="system-message-wrapper__actions"
        fromMessenger
      />
    );
  };

  const renderNegotiationsReopened = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <SystemMessageNegotiationsReopened />
    </div>
  );

  const renderPayoutRescheduledBusinessAndBankDetailsUnverified = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <PayoutRescheduledBusinessAndBankDetailsUnverified
        isBrand={Boolean(isBrand)}
      />
    </div>
  );

  const renderPayoutRescheduledBusinessAndBankDetailsIncomplete = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <PayoutRescheduledBusinessAndBankDetailsIncomplete
        isBrand={Boolean(isBrand)}
      />
    </div>
  );

  const renderPayoutRescheduledInvoiceNotReceived = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <PayoutRescheduledInvoiceNotReceived
        isBrand={Boolean(isBrand)}
      />
    </div>
  );

  const renderPayoutRescheduledAccidentalRelease = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <PayoutRescheduledAccidentalRelease
        isBrand={Boolean(isBrand)}
      />
    </div>
  );

  const renderPayoutRescheduledOther = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <PayoutRescheduledOther
        isBrand={Boolean(isBrand)}
      />
    </div>
  );

  const renderPaymentDetailsAreEmpty = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <SystemMessagePaymentDetailsAreEmpty />
    </div>
  );

  const renderCampaignEnded = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <SystemMessageCampaignEnded />
    </div>
  );

  const renderCcmOutfitDeclined = (): React.ReactNode => (
    <div className="system-message-wrapper__component">
      <SystemMessageCcmOutfitDeclined />
    </div>
  );

  const renderCcmCampaignParticipation = (): React.ReactNode => (
    <React.Fragment>
      <div className="system-message-wrapper__component">
        <SystemMessageCcmCampaignParticipation
          type="simple"
        />
      </div>
      <div className="system-message-wrapper__component">
        <SystemMessageCcmCampaignParticipation
          type="verbose"
        />
      </div>
    </React.Fragment>
  );

  const context = React.useMemo<SystemMessageWrapperContextAlias>(() => ({
    userType,
    campaign,
    subcampaign,
    message: updatedMessage,
    influencerProfile,
    isBrand,
    renderAvatar,
    renderDisclaimer,
    renderInfluencerActions
  }), [
    userType,
    campaign,
    subcampaign,
    updatedMessage,
    influencerProfile,
    isBrand
  ]);

  return (
    <SystemMessageWrapperContext.Provider value={context}>
      <div className="system-message-wrapper">
        {
          messageType === 'negotiation_reopen'
            && renderNegotiationsReopened()}
        {
          messageType === 'payout_rescheduled_business_and_bank_details_unverified'
            && renderPayoutRescheduledBusinessAndBankDetailsUnverified()
        }
        {
          messageType === 'payout_rescheduled_business_and_bank_details_incomplete'
            && renderPayoutRescheduledBusinessAndBankDetailsIncomplete()
        }
        {
          messageType === 'payout_rescheduled_invoice_not_received'
            && renderPayoutRescheduledInvoiceNotReceived()
        }
        {
          messageType === 'payout_rescheduled_accidental_release'
            && renderPayoutRescheduledAccidentalRelease()
        }
        {
          messageType === 'payout_rescheduled_other'
            && renderPayoutRescheduledOther()
        }
        {
          messageType === 'payment_details_are_empty'
            && renderPaymentDetailsAreEmpty()
        }
        {
          messageType === 'ccm_campaign_ended' && campaign.campaignType === 'ccm'
            && renderCampaignEnded()
        }
        {
          messageType === 'ccm_outfit_declined' && campaign.campaignType === 'ccm'
            && renderCcmOutfitDeclined()
        }
        {
          messageType === 'ccm_campaign_participation' && campaign.campaignType === 'ccm'
            && renderCcmCampaignParticipation()
        }
      </div>
      {renderFooter()}
    </SystemMessageWrapperContext.Provider>
  );
};

export default SystemMessageWrapper;
